import { isNumber } from 'app/[lang]/[esite]/(core)/account/(with-nav)/saved-lists/utils';
import {
  TAX_EXCLUDED_PCC_TAX_DISCLAIMER,
  TAX_INCLUDED_PCC_TAX_DISCLAIMER
} from './constants';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { PRODUCT_TYPE } from 'constants/common';
import {
  PriceData,
  ProductsPriceData,
  SearchAddToCartItemData,
  SearchSelectItemData
} from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';
import { getSeoUrl, normalizeCommerceLink } from 'utils/links';
import { HEADLESS_PRODUCT_PAGE_PATH } from 'constants/links';
import { OrderItemPart } from 'services/cart/types';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';

export const getEctMessageCode = ({
  isGstAvailable,
  gstIncludesTax
}: {
  isGstAvailable: boolean;
  gstIncludesTax: boolean;
}) => {
  const shouldShowGSTMessage = isGstAvailable === true;

  if (!shouldShowGSTMessage) {
    return;
  }

  if (shouldShowGSTMessage) {
    if (gstIncludesTax === true) {
      return TAX_INCLUDED_PCC_TAX_DISCLAIMER;
    }

    if (gstIncludesTax === false) {
      return TAX_EXCLUDED_PCC_TAX_DISCLAIMER;
    }
  }
};

export const shouldShowDiscountedPrice = (discountAmount, unitPrice) => {
  const discountAmountValue = discountAmount?.match(/\d+(\.\d+)?/)?.[0];
  const isValidDiscountAmount =
    isNumber(discountAmountValue) && parseFloat(discountAmountValue) > 0;

  return isValidDiscountAmount && !isEmpty(unitPrice);
};

export const productCategoryCommonLogic = (
  productPrice,
  isRegistrationPendingApproval,
  type
) => {
  const hasPrice =
    !!productPrice?.price.unitPrice && !isEmpty(productPrice?.price.unitPrice);
  const hasReplacement = productPrice?.replacementParts?.length > 0;
  const showAddCart =
    (hasPrice && !hasReplacement) || isRegistrationPendingApproval;
  const { availabilityMessage = '', showContactDealerPopUp = true } =
    productPrice?.availability?.[0] || {};

  const isNonReturnable = productPrice?.partLevelFlags?.isNonReturnable;
  const isReplacedPart = productPrice?.replacementParts?.length > 0;
  const isRemanufacturedPart = productPrice?.partLevelFlags?.isRemanPart;
  const isIndirectReplacement =
    productPrice?.partLevelFlags?.isIndirectReplacement;
  const isKit = type === PRODUCT_TYPE.PACKAGE_BEAN;
  const hasIcons =
    isNonReturnable || isReplacedPart || isRemanufacturedPart || isKit;
  return {
    showAddCart,
    hasReplacement,
    isIndirectReplacement,
    hasIcons,
    availabilityMessage,
    showContactDealerPopUp,
    isNonReturnable,
    isReplacedPart,
    isKit,
    isRemanufacturedPart
  };
};

export const getPDPUrl = ({
  esiteParams,
  isHeadlessPDPEnabled,
  productNumber,
  productURL
}) => {
  const esiteUrl = getSeoUrl(esiteParams);
  return isHeadlessPDPEnabled
    ? `${esiteUrl}${HEADLESS_PRODUCT_PAGE_PATH}/${productNumber}`
    : normalizeCommerceLink(productURL);
};

export const getFormattedSearchSelectItem = (
  orderItem:
    | OrderItemPart
    | { orderItemId?: string | number; orderId?: string | number },
  productPriceData: ProductsPriceData & ProductDealerPriceAvailability
): SearchSelectItemData => {
  const { orderItemId, orderId } = orderItem || {};
  const { partNumber, productPriceData: priceData } = productPriceData || {};

  const { quantity, weight, availability, unformattedTotalPrice } =
    priceData || {};

  return {
    orderItemId: orderItemId || orderId,
    partNumber: partNumber || productPriceData?.partNumber,
    quantity: quantity || productPriceData?.quantity,
    weight: weight?.toString() || productPriceData?.weight?.toString(),
    availabilityMessage:
      availability?.[0]?.availabilityMessage ||
      productPriceData?.availability?.[0]?.availabilityMessage,
    totalPrice:
      (unformattedTotalPrice && Number(unformattedTotalPrice)) ||
      (productPriceData?.unformattedUnitPrice &&
        Number(productPriceData?.unformattedUnitPrice))
  };
};

export const getFormattedItemSummary = (
  productPriceData: ProductsPriceData
): Product => {
  const {
    productPriceData: priceData,
    serialNumber,
    fitmentCheck,
    listNameAndId,
    ...rest
  } = productPriceData;

  return { ...rest, categoryPath: priceData?.categoryPath };
};

export const getFormattedItemData = (
  cartId: string,
  productPriceData:
    | ProductsPriceData
    | PriceData[]
    | ProductDealerPriceAvailability[],
  currency?: string
): SearchAddToCartItemData | SearchAddToCartItemData[] => {
  let priceData;
  if (productPriceData) {
    if (Array.isArray(productPriceData)) {
      priceData = productPriceData;
    } else {
      priceData = [productPriceData];
    }
  } else {
    priceData = [];
  }
  const itemPriceData = priceData?.map((item) => {
    return {
      orderId: cartId,
      orderItemPriceAvailabilityTotals: {
        currency: currency,
        orderItemPrices: [
          {
            priceWithoutGST: {
              unformatted: {
                unitPrice:
                  item?.unformattedTotalPrice &&
                  Number(item?.unformattedTotalPrice)
              }
            },
            availability: [
              {
                availabilityMessage:
                  item?.availability?.[0]?.availabilityMessage
              }
            ]
          }
        ]
      },
      orderItems: [
        {
          quantity: item?.quantity
        }
      ],
      partNumber: item?.partNumber
    };
  });

  return itemPriceData;
};
